import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, PackageCheck, RotateCcw, Tag, Wallet } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Empty, ErrorBlock, Spinner } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { api } from "../../api/client.ts";
import { useMe } from "../../api/queries.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 站内消息
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **网页上正文直接铺开，不折成两行。**
 *    手机版必须 `line-clamp-2`，屏幕就那么宽；网页上一行能放六七十个字，
 *    折起来反而要多点一下才知道说的是什么 —— 而这些消息（"你有 3 个规格
 *    正在亏本卖"）恰恰是**看到就要立刻处理**的。
 *
 * ⚠️ 加了「未读」筛选：网页上一次看几十条，全部混在一起时
 *    "今天有什么新事"根本看不出来。
 */

interface Message {
  id: number;
  orgId: number | null;
  kind: string;
  title: string;
  body: string;
  link: string;
  readAt: string | null;
  createdAt: string;
}

/** ⚠️ 认不出的类型给个中性图标，不要让整页崩掉 —— 以后加新类型是常事。 */
const ICON: Record<string, LucideIcon> = {
  price_change: AlertTriangle,
  product_reviewed: Tag,
  order: PackageCheck,
  refund: RotateCcw,
  withdrawal: Wallet,
};

const TONE: Record<string, string> = {
  price_change: "text-amber-600 bg-amber-50",
  product_reviewed: "text-brand-600 bg-brand-50",
  order: "text-emerald-600 bg-emerald-50",
  refund: "text-sky-600 bg-sky-50",
  withdrawal: "text-brand-600 bg-brand-50",
};

function when(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "刚刚";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`;
  return d.toLocaleString("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function DesktopMessages() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const [onlyUnread, setOnlyUnread] = useState(false);

  const list = useQuery({
    queryKey: ["messages"],
    queryFn: () => api.get<{ items: Message[]; nextCursor: number | null }>("/me/messages"),
    enabled: Boolean(me),
  });

  const read = useMutation({
    mutationFn: (ids?: number[]) => api.post("/me/messages/read", ids ? { ids } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messages"] });
      void qc.invalidateQueries({ queryKey: ["messages", "unread"] });
    },
  });

  if (!me)
    return (
      <AccountShell title="站内消息">
        <Empty
          title="还没登录"
          hint="登录后可以收到调价提醒、发货通知"
          action={{ label: "去登录", to: "/login" }}
          compact
        />
      </AccountShell>
    );
  if (list.isPending)
    return (
      <AccountShell title="站内消息">
        <Spinner compact />
      </AccountShell>
    );
  if (list.error)
    return (
      <AccountShell title="站内消息">
        <ErrorBlock error={list.error} />
      </AccountShell>
    );

  const all = list.data?.items ?? [];
  const unread = all.filter((m) => !m.readAt).length;
  const items = onlyUnread ? all.filter((m) => !m.readAt) : all;

  return (
    <AccountShell title="站内消息">
      <div className="mb-4 flex items-center gap-2">
        {[
          { key: false, label: `全部（${all.length}）` },
          { key: true, label: `未读（${unread}）` },
        ].map((f) => (
          <button
            key={String(f.key)}
            onClick={() => setOnlyUnread(f.key)}
            className={cn(
              "rounded-lg px-3.5 py-1.5 text-[14px] transition-colors",
              onlyUnread === f.key
                ? "bg-brand-50 font-medium text-brand-700"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground",
            )}
          >
            {f.label}
          </button>
        ))}
        {unread > 0 && (
          <button
            className="ml-auto text-[14px] text-brand-600 transition-colors hover:text-brand-700"
            onClick={() => read.mutate(undefined)}
          >
            全部标为已读
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <Empty
          title={onlyUnread ? "没有未读消息" : "还没有消息"}
          hint="上游调价、商品审核、发货、退款都会发到这里"
          compact
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {items.map((m) => {
            const Icon = ICON[m.kind] ?? PackageCheck;
            return (
              <button
                key={m.id}
                className={cn(
                  "flex w-full gap-4 border-b border-border px-5 py-4 text-left transition-colors last:border-b-0 hover:bg-secondary/50",
                  !m.readAt && "bg-brand-50/40",
                )}
                onClick={() => {
                  /**
                   * ⚠️ **先标已读再跳。** 反过来的话页面已经切走，
                   *    请求可能被打断，红点会一直挂着。
                   */
                  if (!m.readAt) read.mutate([m.id]);
                  if (m.link) navigate({ to: m.link, search: {} as never });
                }}
              >
                <span
                  className={cn(
                    "flex size-9 shrink-0 items-center justify-center rounded-lg",
                    TONE[m.kind] ?? "bg-secondary text-muted-foreground",
                  )}
                >
                  <Icon className="size-[18px]" />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-3">
                    <p className={cn("min-w-0 flex-1 text-[15px]", !m.readAt && "font-medium")}>
                      {m.title}
                    </p>
                    <span className="shrink-0 text-[12px] text-muted-foreground">
                      {when(m.createdAt)}
                    </span>
                  </div>
                  {/* ⚠️ 网页上正文完整展开 —— 看到就要处理的事，不该再折一层。 */}
                  {m.body && (
                    <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground break-anywhere">
                      {m.body}
                    </p>
                  )}
                  {m.link && <p className="mt-1.5 text-[13px] text-brand-600">去处理 →</p>}
                </div>

                {/* 🔴 未读要一眼看得出来 —— 列表里全是灰字的话他不知道哪条是新的。 */}
                {!m.readAt && (
                  <span className="mt-2 size-2 shrink-0 rounded-full bg-destructive" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </AccountShell>
  );
}
